/**
 * lineEdits — a clerk's hand-typed RATE and SETS on one priced band of one
 * payout, kept until the payout is paid.
 *
 * Mikko, 2026-10-03, comparing a screen against the workbook's own payslip
 * (REGONDOLA: the app priced 6 sets at P8 ₱130 and 4 at P9 ₱150, the workbook
 * had 4 and 6 — ₱40, ten sets apart on one pants type): "in How the sets come
 * up, it should be editable too ... rate and sets are edited, amount is auto
 * computed, so once the rate and sets are edited, the Work, additional charge,
 * 12% BIR and Transportation should change accordingly."
 *
 * THE WORKBOOK ALWAYS ALLOWED THIS, and `IPayoutLine.rate` says so in its own
 * doc: "Editable per payslip: the card is the default, not a lock." So this is
 * not a new power, it is the old one on the screen where the figures are read.
 *
 * AN EDIT IS A CLERK'S WORD OVER THE DERIVATION, and it is kept honest three
 * ways:
 *
 *   - It is stored with the figure it replaced (`baseRate` / `baseQty`). If a
 *     later QC pass moves the derived figure, the edit is STALE and the screen
 *     says "derived is now N" rather than letting a typed number silently hide
 *     work that landed after it.
 *   - Typing the derived figure back removes the edit — there is no separate
 *     reset for a single field.
 *   - The line KEEPS its `scanKeys` and `wipIds`, so paying it still spends
 *     every pass behind it. A line edited to zero is kept (it prints nothing,
 *     `payslipDoc` drops qty 0) precisely so its passes are not priced again
 *     next week.
 *
 * Unlike Payable (payableQty.ts) a band's sets MAY go above the derived figure:
 * moving two sets from one pants band to another needs the destination to rise,
 * and that is the whole use. The cost of that freedom is that the figure is a
 * person's, so the line's source becomes "manual" and the Pay confirmation
 * counts how many lines were edited.
 *
 * KEYED ON THE PAYOUT REFERENCE, then the rate card's `rateKey`. One JSON map
 * in `iSyn2_SubconPayrollSettings` like the holds and the pants types — no new
 * list or column, no Migration Valve step. Pure and I/O-free.
 */
import type { IPayoutLine } from "./payrollMath";

/** The settings-list key the stored edits live under. */
export const LINE_EDITS_KEY = "lineEdits";

export interface ILineEdit {
  /** The typed rate, when it differs from the derived one. */
  rate?: number;
  /** The typed sets, when they differ from the derived ones. */
  qty?: number;
  /** The derived rate when `rate` was typed — to tell when it has since moved. */
  baseRate?: number;
  /** The derived sets when `qty` was typed. */
  baseQty?: number;
}

/** rateKey -> the edit on that band. */
export type LineEditsForPayout = Record<string, ILineEdit>;
/** payout reference -> its edits. */
export type StoredLineEdits = Record<string, LineEditsForPayout>;

const finite = (v: unknown): number | undefined => {
  const n = typeof v === "number" ? v : v === null || v === undefined || v === "" ? NaN : Number(v);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
};

/** Parse the stored blob. Never throws; an unreadable row means nothing was edited. */
export function parseStoredLineEdits(text: string | null | undefined): StoredLineEdits {
  if (!text) return {};
  try {
    const raw: unknown = JSON.parse(text);
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
    const out: StoredLineEdits = {};
    for (const [ref, bands] of Object.entries(raw as Record<string, unknown>)) {
      if (!ref.trim() || !bands || typeof bands !== "object" || Array.isArray(bands)) continue;
      const one: LineEditsForPayout = {};
      for (const [rateKey, e] of Object.entries(bands as Record<string, unknown>)) {
        if (!rateKey.trim() || !e || typeof e !== "object") continue;
        const o = e as Record<string, unknown>;
        const edit: ILineEdit = {};
        const rate = finite(o.rate), qty = finite(o.qty);
        if (rate !== undefined) { edit.rate = rate; const b = finite(o.baseRate); if (b !== undefined) edit.baseRate = b; }
        if (qty !== undefined) { edit.qty = Math.floor(qty); const b = finite(o.baseQty); if (b !== undefined) edit.baseQty = b; }
        if (edit.rate !== undefined || edit.qty !== undefined) one[rateKey] = edit;
      }
      if (Object.keys(one).length) out[ref] = one;
    }
    return out;
  } catch {
    return {};
  }
}

/**
 * The derived lines with a payout's edits laid over them. Lines nobody edited
 * come back as the SAME objects; an edit never creates a line the derivation
 * did not produce (a band nothing was priced on stays absent).
 */
export function applyLineEdits(
  lines: readonly IPayoutLine[], edits: LineEditsForPayout | undefined,
): IPayoutLine[] {
  if (!edits) return [...lines];
  return lines.map(l => {
    const e = edits[l.rateKey];
    if (!e) return l;
    const qty = e.qty !== undefined ? e.qty : l.qty;
    const rate = e.rate !== undefined ? e.rate : l.rate;
    if (qty === l.qty && rate === l.rate) return l;
    return { ...l, rate, qty, source: qty !== l.qty ? "manual" as const : l.source };
  });
}

/** Whether a band is a design (top) band — everything that is not PANTS. */
const isTopBand = (l: Pick<IPayoutLine, "group">): boolean => l.group !== "PANTS";

/**
 * The sets DELIVERED after the edits, for the transpo gate. Delivery is counted
 * once per pass on the DESIGN side (`IPayoutDraft.setsDelivered`), and a band's
 * sets on that side are exactly those sets — 17 + 1 + 14 + 5 + 2 + 3 = 42 on
 * REGONDOLA's payout, the same 42 — so a change to a design band's sets moves
 * the delivered count by the same amount. A pants-band edit moves nothing: it
 * re-divides trousers, it does not deliver or un-deliver a set.
 */
export function deliveredAfterEdits(
  setsDelivered: number, derived: readonly IPayoutLine[], edited: readonly IPayoutLine[],
): number {
  const top = (ls: readonly IPayoutLine[]): number =>
    ls.reduce((n, l) => n + (isTopBand(l) ? Number(l.qty) || 0 : 0), 0);
  return Math.max(0, setsDelivered + (top(edited) - top(derived)));
}

export interface ILineEditStatus {
  /** The sets were typed. */
  qtyEdited: boolean;
  /** The rate was typed. */
  rateEdited: boolean;
  /** The derived sets, for "was N". */
  derivedQty: number;
  /** The derived rate. */
  derivedRate: number;
  /** The derivation moved since the edit was typed — the edit may be hiding work. */
  stale: boolean;
}

/** What the screen needs to mark an edited band, by rateKey. Unedited bands are absent. */
export function lineEditStatus(
  derived: readonly IPayoutLine[], edits: LineEditsForPayout | undefined,
): Map<string, ILineEditStatus> {
  const out = new Map<string, ILineEditStatus>();
  if (!edits) return out;
  for (const l of derived) {
    const e = edits[l.rateKey];
    if (!e) continue;
    const qtyEdited = e.qty !== undefined && e.qty !== l.qty;
    const rateEdited = e.rate !== undefined && e.rate !== l.rate;
    if (!qtyEdited && !rateEdited) continue;
    const stale = (qtyEdited && e.baseQty !== undefined && e.baseQty !== l.qty)
      || (rateEdited && e.baseRate !== undefined && e.baseRate !== l.rate);
    out.set(l.rateKey, { qtyEdited, rateEdited, derivedQty: l.qty, derivedRate: l.rate, stale });
  }
  return out;
}

/**
 * Record one band's typed figures against the derived line. A figure equal to
 * the derived one is dropped (typing it back is the reset), and a payout with
 * no edits left disappears from the map. Returns a new map.
 */
export function mergeLineEdit(
  stored: StoredLineEdits, ref: string, derived: Pick<IPayoutLine, "rateKey" | "rate" | "qty">,
  change: { rate?: number; qty?: number },
): StoredLineEdits {
  const mine: LineEditsForPayout = { ...(stored[ref] ?? {}) };
  const cur: ILineEdit = { ...(mine[derived.rateKey] ?? {}) };
  if (change.qty !== undefined) {
    const q = Math.max(0, Math.floor(change.qty));
    if (q === derived.qty) { delete cur.qty; delete cur.baseQty; } else { cur.qty = q; cur.baseQty = derived.qty; }
  }
  if (change.rate !== undefined) {
    const r = Math.max(0, change.rate);
    if (r === derived.rate) { delete cur.rate; delete cur.baseRate; } else { cur.rate = r; cur.baseRate = derived.rate; }
  }
  if (cur.qty === undefined && cur.rate === undefined) delete mine[derived.rateKey];
  else mine[derived.rateKey] = cur;
  const next = { ...stored };
  if (Object.keys(mine).length) next[ref] = mine; else delete next[ref];
  return next;
}

/** Forget every edit on a payout (after it is paid, or on a reset). Returns a new map. */
export function clearPayoutEdits(stored: StoredLineEdits, ref: string): StoredLineEdits {
  if (!(ref in stored)) return stored;
  const next = { ...stored };
  delete next[ref];
  return next;
}
